import { createClient, type Session } from '@supabase/supabase-js';
import { create } from 'zustand';

/**
 * Accounts (Supabase). Signing in is optional for play; it's needed to contribute, for the
 * owner's tools and, later, rankings. The publishable key is meant to be public: every table
 * is locked by row level security, and roles can only be set in the database itself.
 */
const SUPABASE_URL = 'https://axgkrvrghaobaryvuess.supabase.co';
const SUPABASE_KEY = 'sb_publishable_A0UPxs99mjEUIaMqTGKhzw_0e0TktIy';

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: 'pkce' },
});

export type Role = 'owner' | 'admin' | null;

interface AccountState {
  ready: boolean;
  session: Session | null;
  role: Role;
  callsign: string | null;
  signInOpen: boolean;
}

export const useAccount = create<AccountState>(() => ({ ready: false, session: null, role: null, callsign: null, signInOpen: false }));

async function loadExtras(session: Session | null): Promise<void> {
  if (!session) {
    useAccount.setState({ role: null, callsign: null });
    return;
  }
  const [role, profile] = await Promise.all([
    supabase.rpc('my_role'),
    supabase.from('profiles').select('callsign').eq('user_id', session.user.id).maybeSingle(),
  ]);
  useAccount.setState({ role: (role.data as Role) ?? null, callsign: (profile.data?.callsign as string | null) ?? null });
}

let started = false;
/** Call once at startup: picks up a saved session or the link the person just clicked. */
export function startAccounts(): void {
  if (started) return;
  started = true;
  supabase.auth.onAuthStateChange((_event, session) => {
    useAccount.setState({ session, ready: true });
    // Supabase advises against awaiting other calls inside this callback.
    setTimeout(() => void loadExtras(session), 0);
  });
  void supabase.auth.getSession().then(({ data }) => {
    useAccount.setState({ session: data.session, ready: true });
    void loadExtras(data.session);
  });
}

const backHere = () => `${location.origin}${location.pathname}`;

/** Emails a one-time sign-in link. No password. */
export async function emailLink(email: string): Promise<string | null> {
  const { error } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { emailRedirectTo: backHere(), shouldCreateUser: true } });
  if (!error) return null;
  if (/rate limit|too many/i.test(error.message)) return 'Too many sign-in emails right now. Wait a few minutes and try again.';
  return "Couldn't send the link. Check the address and try again.";
}

export async function google(): Promise<string | null> {
  const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: backHere() } });
  if (!error) return null;
  if (/not enabled|unsupported provider/i.test(error.message)) return 'Google sign-in is not switched on yet. Use the email link for now.';
  return "Couldn't start Google sign-in. Try the email link instead.";
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

/** What to call someone in the corner of the screen. */
export function displayName(s: AccountState): string {
  if (s.callsign) return s.callsign;
  const email = s.session?.user.email ?? '';
  return email ? email.split('@')[0]!.slice(0, 16) : 'Signed in';
}

export async function setCallsign(callsign: string): Promise<string | null> {
  const uid = useAccount.getState().session?.user.id;
  if (!uid) return 'Sign in first.';
  const value = callsign.trim();
  if (!/^[A-Za-z0-9 _-]{2,20}$/.test(value)) return 'Use 2–20 letters, numbers, spaces, dashes or underscores.';
  const { error } = await supabase.from('profiles').update({ callsign: value }).eq('user_id', uid);
  if (error) return /duplicate|unique/i.test(error.message) ? 'That callsign is taken.' : "Couldn't save it. Try again.";
  useAccount.setState({ callsign: value });
  return null;
}
